import type { ReactNode } from "react";
import { ArrowLeft, Globe, MapPin } from "lucide-react";
import type { OrgStoryView } from "@/lib/orgStory";
import type { OrgSocialLink } from "@/lib/orgSocialHandles";
import { httpUrl, orgTypeLabel } from "@/lib/publicOrganizationProfile";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import CoverTexture from "@/components/primitives/CoverTexture";
import { OrgSocialLinks } from "@/components/organization/social/OrgSocialLinks";
import WorkModelBadge from "@/components/organization/WorkModelBadge";
import { cn } from "@/lib/utils";

interface StoryHeaderProps {
  view: OrgStoryView;
  socialLinks?: OrgSocialLink[];
  onBack?: () => void;
  /** The public page's org name is its only h1; the signed-in page keeps h2. */
  titleAs?: "h1" | "h2";
  /** Buttons on the cover's lower edge, opposite the logo (Save / Compare / Share). */
  actions?: ReactNode;
  /** Beside the name at `lg`, below it on smaller screens — the "Who we're looking for" card. */
  aside?: ReactNode;
}

const monogram = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

/** "https://www.example.com/about" → "example.com/about": what a visitor would type. */
const displayUrl = (url: string) =>
  url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/$/, "");

const nonBlank = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * The story's hero: a full-width cover (the brokerage's personality), the logo
 * overlapping its lower edge, the brand line above a clean Manrope name, and
 * an optional aside beside the name. Cover position follows ProfileHeader's
 * convention: `profileData.coverImagePosition` as `{x, y}` percentages.
 * Without a cover the org's seeded CoverTexture stands in, so the hero keeps
 * its shape.
 */
export default function StoryHeader({ view, socialLinks, onBack, titleAs = "h2", actions, aside }: StoryHeaderProps) {
  const { identity, agentCount } = view;
  const Title = titleAs;
  const coverRaw = view.profileData.coverImagePosition as { x?: unknown; y?: unknown } | null | undefined;
  const coverPos =
    coverRaw && typeof coverRaw.x === "number" && typeof coverRaw.y === "number"
      ? (coverRaw as { x: number; y: number })
      : null;
  const hasCover = !!identity.coverUrl;
  const typeLabel = orgTypeLabel(identity.type);
  const place = [identity.city, identity.state].filter(Boolean).join(", ");
  // Reserved in profile_data; the public RPC does not carry it yet.
  const franchise = nonBlank(view.profileData.franchise);
  const chips = [typeLabel, identity.city].filter(Boolean) as string[];
  // Contact info: the website sits with the social links, not in the side panel.
  const site = httpUrl(identity.website);
  const hasSocial = !!socialLinks && socialLinks.length > 0;
  // The cloud label's badge sits beside the organization type (cloud label spec §4).
  const badge = view.workModel?.badge ?? null;
  const meta = [
    identity.foundedYear ? { key: "est", node: `Est. ${identity.foundedYear}` } : null,
    typeLabel ? { key: "type", node: typeLabel } : null,
    badge ? { key: "work-model", node: <WorkModelBadge badge={badge} /> } : null,
    agentCount != null
      ? { key: "agents", node: `${agentCount.toLocaleString()} agent${agentCount === 1 ? "" : "s"}` }
      : null,
  ].filter(Boolean) as { key: string; node: ReactNode }[];

  return (
    <div>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1.5 rounded-md text-sm text-ink-500 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}
      <SurfaceCard padding="none" className="overflow-hidden rounded-2xl">
        <div data-story-cover className="relative aspect-[2/1] w-full overflow-hidden bg-primary-tint text-primary sm:aspect-[3/1]">
          {hasCover ? (
            <>
              <img
                src={identity.coverUrl!}
                alt=""
                className="absolute inset-0 h-full w-full bg-card object-cover"
                style={{ objectPosition: coverPos ? `${coverPos.x}% ${coverPos.y}%` : "center" }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent"
              />
            </>
          ) : (
            <CoverTexture seed={identity.name} />
          )}
          {chips.length > 0 && (
            <div className="absolute left-4 top-4 flex flex-wrap gap-2 sm:left-6 sm:top-5">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className={cn(
                    "whitespace-nowrap rounded-pill px-3 py-1 text-xs font-semibold",
                    hasCover
                      ? "border border-white/25 bg-white/15 text-white backdrop-blur-md"
                      : "border border-border bg-card/80 text-ink-700",
                  )}
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 pb-6 sm:px-[30px] sm:pb-7">
          <div className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
            <div className="relative z-10 -mt-10 shrink-0 rounded-2xl border-[3px] border-card bg-card shadow-card sm:-mt-[52px]">
              {identity.logoUrl ? (
                <img
                  src={identity.logoUrl}
                  alt={`${identity.name} logo`}
                  className="h-20 w-20 rounded-[13px] bg-white object-contain sm:h-24 sm:w-24"
                />
              ) : (
                <div
                  aria-hidden
                  className="flex h-20 w-20 items-center justify-center rounded-[13px] bg-primary font-heading text-2xl font-semibold text-primary-foreground sm:h-24 sm:w-24"
                >
                  {monogram(identity.name)}
                </div>
              )}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>

          <div className={cn("mt-5", aside && "grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)] lg:items-start")}>
            <div className="min-w-0">
              {franchise && <p className="eyebrow mb-2 text-ink-500">{franchise}</p>}
              <Title className="font-heading text-[28px] font-semibold leading-[1.1] tracking-[-0.02em] text-ink-900 sm:text-[34px]">
                {identity.name}
              </Title>
              {identity.tagline && (
                <p className="mt-2 max-w-[560px] text-base leading-relaxed text-ink-700">{identity.tagline}</p>
              )}
              {(place || meta.length > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13.5px] font-medium tabular-nums text-ink-500">
                  {place && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.9} />
                      {place}
                    </span>
                  )}
                  {meta.map((item, i) => (
                    <span key={item.key} className="inline-flex items-center gap-2.5">
                      {(place || i > 0) && <span aria-hidden className="text-border">·</span>}
                      {item.node}
                    </span>
                  ))}
                </div>
              )}
              {(site || hasSocial) && (
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {hasSocial && <OrgSocialLinks links={socialLinks!} />}
                  {site && (
                    <a
                      href={site}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-ink-700 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Globe className="h-4 w-4 text-gray-400" strokeWidth={1.9} />
                      {displayUrl(site)}
                    </a>
                  )}
                </div>
              )}
            </div>
            {aside}
          </div>
        </div>
      </SurfaceCard>
    </div>
  );
}
